import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const field = "w-full rounded-xl border border-white/10 bg-ink px-4 py-3 text-paper outline-none transition-colors focus:border-volt";
const EMPTY = { name: "", rating: 5, comment: "" };

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1" data-testid="admin-testimonial-rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button type="button" key={n} data-testid={`admin-testimonial-star-${n}`} onClick={() => onChange(n)} aria-label={`${n} bintang`}>
        <Star className={`h-7 w-7 ${n <= value ? "fill-volt text-volt" : "text-zinc-600"}`} />
      </button>
    ))}
  </div>
);

const Form = ({ initial, onDone, onCancel }) => {
  const [form, setForm] = useState(initial);
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, rating: form.rating, comment: form.comment };
      if (form.id) await api.put(`/admin/testimonials/${form.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Testimoni disimpan");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <form onSubmit={save} data-testid="admin-testimonial-form" className="space-y-4 rounded-2xl border border-volt/30 p-5">
      <input data-testid="admin-testimonial-name" required placeholder="Nama" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
      <StarPicker value={form.rating} onChange={(rating) => setForm({ ...form, rating })} />
      <textarea data-testid="admin-testimonial-comment" required rows={4} placeholder="Komentar dari Google Play" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className={field} />
      <div className="flex gap-3">
        <button data-testid="admin-testimonial-save" className="rounded-full bg-volt px-6 py-2.5 font-semibold text-ink">Simpan</button>
        <button type="button" data-testid="admin-testimonial-cancel" onClick={onCancel} className="rounded-full border border-white/15 px-6 py-2.5 text-zinc-200">Batal</button>
      </div>
    </form>
  );
};

export const TestimonialsAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/testimonials").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);
  const remove = async (t) => {
    if (!window.confirm(`Hapus testimoni dari "${t.name}"?`)) return;
    await api.delete(`/admin/testimonials/${t.id}`).catch(() => {});
    toast.success("Testimoni dihapus");
    load();
  };
  return (
    <div data-testid="admin-testimonials-panel" className="space-y-4">
      <p className="text-sm text-zinc-400">Website menampilkan 9 testimoni terbaru.</p>
      {editing ? (
        <Form initial={editing} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
      ) : (
        <button data-testid="admin-testimonial-new" onClick={() => setEditing(EMPTY)} className="flex items-center gap-2 rounded-full bg-volt px-5 py-2.5 text-sm font-semibold text-ink"><Plus className="h-4 w-4" /> Testimoni baru</button>
      )}
      {items.map((t, i) => (
        <div key={t.id} data-testid={`admin-testimonial-row-${i}`} className="flex items-center justify-between gap-4 border-b border-white/[0.07] py-4">
          <div className="min-w-0">
            <p className="font-display text-paper">{t.name} <span className="ml-2 text-sm text-volt">{"★".repeat(t.rating)}</span>{i >= 9 ? <span className="ml-2 text-xs text-zinc-500">(tidak tampil)</span> : null}</p>
            <p className="truncate text-sm text-zinc-400">{t.comment}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button data-testid={`admin-testimonial-edit-${i}`} onClick={() => setEditing(t)} className="grid h-9 w-9 place-items-center rounded-full border border-white/15 hover:border-volt" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`admin-testimonial-delete-${i}`} onClick={() => remove(t)} className="grid h-9 w-9 place-items-center rounded-full border border-white/15 hover:border-red-400" aria-label="Hapus"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
};
